import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { api } from "@/features/utils/fetch-server";
import { useTranslation } from "@/features/utils/i18n";
import { Copy } from "@phosphor-icons/react";
import { useEffect, useState } from "react";
import { toast } from "sonner";

type SystemPromptResponse = {
  agent_name?: string;
  cacheable: string;
  dynamic: string;
  prompt: string;
};

/**
 * Shows the system prompt of a conversation (SYSADMIN only).
 *
 * The prompt is rebuilt on demand from the session — nothing is stored per turn — so the
 * static half is faithful while the <live> half reflects *now*. The dialog says so rather
 * than pretending to be a replay.
 */
export function SystemPromptDialog({
  clientId,
  sessionId,
  open,
  onOpenChange,
}: {
  clientId: string;
  sessionId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useTranslation("calls");
  const [data, setData] = useState<SystemPromptResponse | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    api
      .get(`/api/sessions/v1/${clientId}/sessions/${sessionId}/system-prompt`)
      .then((response) => setData(response as SystemPromptResponse))
      .catch((error: unknown) =>
        toast.error(
          error instanceof Error
            ? error.message
            : t("session.system_prompt_error"),
        ),
      )
      .finally(() => setLoading(false));
  }, [open, clientId, sessionId]);

  const handleCopy = async () => {
    if (!data) return;
    await navigator.clipboard.writeText(data.prompt);
    toast.success(t("session.system_prompt_copied"));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl">
        <DialogHeader>
          <DialogTitle>{t("session.system_prompt")}</DialogTitle>
          <DialogDescription>
            {t("session.system_prompt_description")}
          </DialogDescription>
        </DialogHeader>

        {loading && (
          <div className="text-muted-foreground text-sm">
            {t("session.loading")}
          </div>
        )}

        {!loading && data && (
          <>
            <div className="flex items-center justify-between gap-2">
              <span className="text-muted-foreground text-sm">
                {data.agent_name}
              </span>
              <Button variant="outline" size="sm" onClick={handleCopy}>
                <Copy className="h-4 w-4" />
                {t("session.system_prompt_copy")}
              </Button>
            </div>
            <pre className="max-h-[60vh] overflow-auto whitespace-pre-wrap break-words rounded-md border bg-muted p-4 text-xs">
              {data.prompt}
            </pre>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
